import * as React from "react";
import SortControl from "../containers/DataTableSortControl";
import { messages } from "../../refdata/UserMessages";
import { formatNumber } from "../services/Utility";
export const NoData = (
  <p className="ds-u-margin--2">
    <strong>{messages.information.noRecordsToDisplay}</strong>
  </p>
);

export const Table = ({
  columns,
  resultData,
  resultTotals,
  displayFields,
  allowSort = true,
}: any) => {
  const tableHeadings = displayFields.map((f: string) =>
    HeaderCol(f, columns, allowSort),
  );
  // NOTE ON 'KEY' FOR TABLE ROWS
  // React wants a unique key. We can't rely on a consistent key (a typical ID) with our data.
  // Therefore, we are choosing to create a computed key based on the index in the map function.
  // This is a tradeoff and is valid in these cases with no real key, and no need to update rows of data.
  // When data is replaced, it is blown away and replaced with the entire result of an API call/query.
  const tableRows = resultData.map((r: any, i: number) => {
    if (r.COST === null) {
      r.COST = "N/A";
    }
    return TableRow(r, i, displayFields, columns, `table-row-${i + 1}`);
  });
  // prep the total rows
  let totalRows: any[] = [];
  if (resultTotals) {
    // find subtotal rows (if any)
    let subtotalCol: string = "";
    Object.keys(resultTotals).forEach((key: string) => {
      if (key.substr(0, 12) === "subtotal_by_") {
        subtotalCol = key.substr(12);
      }
    });
    // add subtotal if applicable
    if (subtotalCol.length > 0) {
      resultTotals[`subtotal_by_${subtotalCol}`].forEach(
        (st: any, i: number) => {
          const subtotalRowData = st;
          const subtotalValue = st[subtotalCol];
          subtotalRowData[subtotalCol] = st.count;
          // Label the first column with appropriate language "TOTAL XXX"
          subtotalRowData[displayFields[0]] = `TOTAL ${subtotalValue}`;
          totalRows.push(
            TableRow(
              subtotalRowData,
              88888888 + i,
              displayFields,
              columns,
              "ds-u-font-weight--bold",
            ),
          );
        },
      );
    }
    // insert overall total row
    resultTotals[displayFields[0]] = "TOTAL";
    if (subtotalCol.length > 0) {
      resultTotals[subtotalCol] = resultTotals.count;
    }
    totalRows.push(
      TableRow(
        resultTotals,
        9999999,
        displayFields,
        columns,
        "ds-u-font-weight--bold",
      ),
    );
  }
  // do we display a message for units in thousands of $US
  let unitMessage;
  let specialNoteMessage;
  // let resultsMessage;
  const units = displayFields.map((f: string) => {
    const haveColData = columns && columns[f] ? true : false;
    return haveColData ? columns[f]["x-number-unit"] : null;
  });

  const haveDollarsInThousands = units.indexOf("Thousands of US Dollars") > -1;
  if (haveDollarsInThousands) {
    unitMessage = (
      <p>
        <strong>* {messages.information.usDollarInThousands}</strong>
      </p>
    );
  }
  //TODO: create forEach for array of messages for future flexibility
  const specialNote = displayFields
    .map((f: string) => {
      const haveColData = columns && columns[f] ? true : false;
      return haveColData ? columns[f]["x-special-note"] : null;
    })
    .filter((e: string) => {
      return e;
    });

  if (specialNote.length > 0) {
    specialNoteMessage = (
      <p>
        <strong>** {specialNote}</strong>
      </p>
    );
  }

  return (
    <React.Fragment>
      <table className="ds-c-table ds-c-table--borderless fdic-data-table fixed-header">
        <thead>
          {unitMessage || specialNoteMessage ? (
            <tr className="data-table-messages">
              {unitMessage}
              {specialNoteMessage}
            </tr>
          ) : (
            ""
          )}
          <tr>{tableHeadings}</tr>
        </thead>
        <tbody>
          {tableRows}
          {totalRows}
        </tbody>
      </table>
    </React.Fragment>
  );
};
//TODO: define columns type in an interface since its being used multiple times
const HeaderCol = (
  colName: string,
  columns: { [key: string]: any },
  sortable = true,
) => {
  const haveColData = columns && columns[colName] ? true : false;
  var title;
  let unitsAsterisk = "";
  let specialNoteAsterisk = "";
  if (haveColData) {
    const colData = columns[colName];
    title = colData.title;
    unitsAsterisk =
      colData["x-number-unit"] === "Thousands of US Dollars" ? "*" : "";
    specialNoteAsterisk = colData["x-special-note"] ? "**" : "";
  } else {
    title = colName;
  }
  const sortControl = sortable ? (
    <SortControl
      fieldName={colName}
      fieldTitle={title + unitsAsterisk + specialNoteAsterisk}
    />
  ) : null;
  return (
    <th key={colName} scope="col">
      {sortControl}
      <span className="data-table-header-text ds-u-margin-left--1">
        {title + unitsAsterisk + specialNoteAsterisk}
      </span>
    </th>
  );
};

const TableRow = (
  rowData: any,
  computedKey: number,
  displayFields: string[],
  columns: { [key: string]: any },
  cssClass?: string,
) => {
  const tableCols = displayFields.map((f: any) => {
    const colType = columns && columns[f] ? columns[f].type : null;
    return TableCol(f, rowData[f], colType);
  });
  return (
    <tr key={computedKey} className={cssClass}>
      {tableCols}
    </tr>
  );
};

const TableCol = (colName: string, value: any, colType: any) => {
  const typeIsNum = colType && typeIsNumeric(colType);
  const displayValue = typeIsNum ? formatNumber(value) : value;
  return (
    <td key={colName} className="ds-u-text-align--center">
      {displayValue}
    </td>
  );
};

const typeIsNumeric = (type: string) => {
  const numericTypes = ["number", "integer", "float", "long"];
  return numericTypes.indexOf(type) > -1;
};
